import {
  IPaymentIntent,
  PaymentIntentFlow,
  PaymentIntentStatus,
} from '@/schemas/PaymentIntent';
import { execGet, execPost } from '@/services/utils/RequestUtils';

import { AuthorizationError } from './errors/AuthorizationError';
import { IntentAlreadyProcessedError } from './errors/IntentAlreadyProcessedError';
import { parseApiPaymentIntent } from './helpers/PaymentIntentHelper';
import { ApiPaymentIntent } from './schemas/ApiPaymentIntent';
import { AuthorizationPayload } from './schemas/AuthorizationPayload';

interface PaymentIntentStatusResponse {
  readonly status: PaymentIntentStatus;
}

export async function getPaymentIntentByNonceToken(
  nonceToken: string,
  accessToken: string,
): Promise<IPaymentIntent> {
  const response = await execGet<ApiPaymentIntent>(
    `paymentintent?nonceToken=${nonceToken}`,
    accessToken,
  );

  return parseApiPaymentIntent(response);
}

export async function getPaymentIntentById(
  paymentIntentId: string,
  accessToken: string,
): Promise<IPaymentIntent> {
  const response = await execGet<ApiPaymentIntent>(
    `paymentintent/${paymentIntentId}`,
    accessToken,
  );

  const intent = parseApiPaymentIntent(response);

  // After adding a new payment intrument, the intent will have its
  // status updated. For management flow, we want the user to continue
  // in the page and add multiple cards and leave only when he wants
  if (response?.customer?.flow === PaymentIntentFlow.Manage) {
    return intent;
  }

  if (!intent.isPayable) {
    throw new IntentAlreadyProcessedError(intent.id);
  }

  return intent;
}

export async function getPaymentIntentStatusById(
  paymentIntentId: string,
  accessToken: string,
): Promise<PaymentIntentStatus> {
  const response = await execGet<PaymentIntentStatusResponse>(
    `paymentintent/${paymentIntentId}/status`,
    accessToken,
  );

  return response.status;
}

export function paymentInstrumentAuthorize(
  body: AuthorizationPayload,
  accessToken: string,
): Promise<void> {
  return execPost<void>(
    `paymentintent/${body.paymentIntentId}/authorize`,
    body,
    accessToken,
  ).catch((err) => {
    throw new AuthorizationError(body.paymentDataTypeId, err);
  });
}
